import { useState } from "react";
import { Field } from "react-final-form";
import Box from "@mui/material/Box";

import { slate, teal } from "../../../theme/colors";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * Whole-number count with the up/down stepper column from the entity Edit
 * Member form's Ownership % input (arrows only — no presets, no slider).
 * Stores a plain integer clamped to `min`; an optional `suffix` ("deals") sits
 * inside the field left of the arrows.
 *
 * Draws NO border or background of its own: it is built to sit inside a
 * PanelRow's blue edit cell and read like the other inline inputs there.
 */
const clampInt = (v, min) => Math.max(min, Math.round(Number(v) || 0));

const StepChevron = ({ up }) => (
  <svg
    width="9"
    height="6"
    viewBox="0 0 12 8"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={up ? "M1 6.5 6 1.5 11 6.5" : "M1 1.5 6 6.5 11 1.5"} />
  </svg>
);

const stepBtnSx = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: 0,
  background: "transparent",
  color: slate[400],
  cursor: "pointer",
  padding: 0,
  transition: "background .12s, color .12s",
  "&:hover": { background: teal[50], color: teal[700] },
  "&:active": { background: teal[100] },
};

// A local `raw` buffer owns the text while focused so a half-typed value isn't
// reformatted under the caret; blur hands display back to the stored number.
const CountInput = ({ value, onChange, min, suffix, ariaLabel, disabled }) => {
  const isNum = typeof value === "number" && !isNaN(value);
  const [raw, setRaw] = useState(null);
  const shown = raw != null ? raw : isNum ? String(value) : "";

  const bump = (dir) => {
    if (disabled) return;
    setRaw(null);
    onChange(clampInt((isNum ? value : 0) + dir, min));
  };

  return (
    <Box
      className="rff-count-stepper"
      sx={{ position: "relative", width: "100%", minWidth: 0 }}
    >
      <Box
        component="input"
        type="text"
        inputMode="numeric"
        aria-label={ariaLabel}
        disabled={disabled}
        value={shown}
        onChange={(e) => {
          const next = e.target.value;
          setRaw(next);
          const parsed = parseInt(next.replace(/[^0-9]/g, ""), 10);
          onChange(
            next.trim() === "" || isNaN(parsed)
              ? undefined
              : clampInt(parsed, min),
          );
        }}
        onBlur={() => setRaw(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") {
            e.preventDefault();
            bump(1);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            bump(-1);
          }
        }}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          // Right inset clears the suffix + arrow column.
          padding: `8.5px ${suffix ? 68 : 30}px 8.5px 14px`,
          border: 0,
          background: "transparent",
          fontFamily: "inherit",
          fontSize: 11,
          color: "inherit",
          outline: "none",
          appearance: "none",
          fontVariantNumeric: "tabular-nums",
        }}
      />
      {suffix && (
        <Box
          sx={{
            position: "absolute",
            right: 32,
            top: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            color: slate[400],
            fontSize: 11,
            pointerEvents: "none",
          }}
        >
          {suffix}
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 26,
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${slate[200]}`,
          overflow: "hidden",
        }}
      >
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          aria-label="Increase"
          onClick={() => bump(1)}
          sx={stepBtnSx}
        >
          <StepChevron up />
        </Box>
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          aria-label="Decrease"
          onClick={() => bump(-1)}
          sx={{ ...stepBtnSx, borderTop: `1px solid ${slate[200]}` }}
        >
          <StepChevron />
        </Box>
      </Box>
    </Box>
  );
};

const RffCountStepperField = ({
  name,
  label,
  helperText,
  suppressGrid,
  min = 0,
  suffix,
  disabled,
}) => (
  <RffInputWrapper
    label={label}
    helperText={helperText}
    suppressGrid={suppressGrid}
  >
    <Field name={name} parse={(v) => (v === "" ? undefined : v)}>
      {({ input }) => (
        <CountInput
          value={input.value}
          onChange={input.onChange}
          min={min}
          suffix={suffix}
          ariaLabel={label || name}
          disabled={disabled}
        />
      )}
    </Field>
  </RffInputWrapper>
);

export default RffCountStepperField;
